import { useQuery } from '@tanstack/react-query'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext'
import { API_ENDPOINTS } from '../../../constants/api'
import { PREMIUM_MARKETS_ENABLED } from '../../create/constants/matchDetails.constants'

type AccountUserEnvelope = {
  success?: boolean
  data?: { consumerBilling?: { planTier?: string } | null } | null
}

const QUERY_KEY = ['proStatus'] as const

/**
 * Shared billing-snapshot query. Returns the raw `consumerBilling` object (not a derived
 * boolean) so the ['proStatus'] cache entry keeps the SAME shape CoinStoreModal reads — a
 * divergent shape on a shared key lets the two clobber each other in cache.
 */
function useProBillingQuery() {
  const { isSignedIn, isLoaded } = useSupabaseAuth()
  const apiClient = useApiClient()

  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient<AccountUserEnvelope>(API_ENDPOINTS.accounts.user)
      return res.data?.data?.consumerBilling ?? null
    },
    enabled: isLoaded && isSignedIn === true,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * True only when the user has a confirmed PRO plan.
 *
 * Fails CLOSED: while the billing snapshot is still loading (or absent) this returns false so
 * premium markets stay locked. Assuming Pro on an unknown let non-Pro users tap premium picks
 * during the cold-start fetch window and carry them all the way to the final create call, where
 * the server rejected them with a snackbar. Pair with useIsProUserLoading() to show a neutral
 * loading affordance during that (cached-after-first-fetch) window instead of a hard lock flash.
 */
export function useIsProUser(): boolean {
  const { data } = useProBillingQuery()

  if (!PREMIUM_MARKETS_ENABLED) return true
  return data?.planTier === 'PRO'
}

/**
 * True while Pro status is still being resolved — use to render a loading/skeleton affordance
 * (rather than a hard "locked" state) so confirmed-Pro users don't see premium briefly lock on
 * a cold start. After the first fetch the result is cached, so this is false on subsequent reads.
 */
export function useIsProUserLoading(): boolean {
  const { isLoading } = useProBillingQuery()

  if (!PREMIUM_MARKETS_ENABLED) return false
  return isLoading
}
